// US federal holidays computed per year, with observed-date shifts:
// Saturday holidays are observed the Friday before, Sunday holidays the
// Monday after. This is the app-wide default holiday calendar until
// tenants can provide their own list through Settings.

export interface UsFederalHoliday {
  key: string
  label: string
  /** Statutory date, ISO YYYY-MM-DD. */
  date: string
  /** Date the holiday is observed, ISO YYYY-MM-DD. */
  observedDate: string
}

const SATURDAY = 6
const SUNDAY = 0

function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day))
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function nthWeekdayOfMonth(year: number, month: number, weekday: number, nth: number): Date {
  const firstWeekday = utcDate(year, month, 1).getUTCDay()
  const offset = (weekday - firstWeekday + 7) % 7
  return utcDate(year, month, 1 + offset + (nth - 1) * 7)
}

function lastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
  const lastDay = new Date(Date.UTC(year, month, 0))
  const offset = (lastDay.getUTCDay() - weekday + 7) % 7
  return utcDate(year, month, lastDay.getUTCDate() - offset)
}

function observedDate(date: Date): Date {
  const day = date.getUTCDay()
  if (day === SATURDAY) return new Date(date.getTime() - 24 * 60 * 60 * 1000)
  if (day === SUNDAY) return new Date(date.getTime() + 24 * 60 * 60 * 1000)
  return date
}

function holiday(key: string, label: string, date: Date): UsFederalHoliday {
  return { key, label, date: toIsoDate(date), observedDate: toIsoDate(observedDate(date)) }
}

const holidaysByYear = new Map<number, UsFederalHoliday[]>()

export function getUsFederalHolidaysForYear(year: number): UsFederalHoliday[] {
  const cached = holidaysByYear.get(year)
  if (cached) return cached

  const holidays: UsFederalHoliday[] = [
    holiday('new-years-day', "New Year's Day", utcDate(year, 1, 1)),
    holiday('mlk-day', 'Martin Luther King Jr. Day', nthWeekdayOfMonth(year, 1, 1, 3)),
    holiday('presidents-day', "Washington's Birthday", nthWeekdayOfMonth(year, 2, 1, 3)),
    holiday('memorial-day', 'Memorial Day', lastWeekdayOfMonth(year, 5, 1)),
    holiday('juneteenth', 'Juneteenth', utcDate(year, 6, 19)),
    holiday('independence-day', 'Independence Day', utcDate(year, 7, 4)),
    holiday('labor-day', 'Labor Day', nthWeekdayOfMonth(year, 9, 1, 1)),
    holiday('columbus-day', 'Columbus Day', nthWeekdayOfMonth(year, 10, 1, 2)),
    holiday('veterans-day', 'Veterans Day', utcDate(year, 11, 11)),
    holiday('thanksgiving', 'Thanksgiving Day', nthWeekdayOfMonth(year, 11, 4, 4)),
    holiday('christmas', 'Christmas Day', utcDate(year, 12, 25)),
  ]

  holidaysByYear.set(year, holidays)
  return holidays
}

/**
 * `observed` dates are when offices actually close; `calendar` dates are the
 * statutory dates themselves.
 */
export type HolidayDateKind = 'observed' | 'calendar'

/**
 * Holiday dates of the given kind that fall within the given calendar year.
 * Observance can cross year boundaries (a Saturday January 1 is observed the
 * prior December 31), so adjacent years contribute to the observed set.
 */
export function getUsFederalHolidayDatesForYear(
  year: number,
  kind: HolidayDateKind = 'observed'
): string[] {
  const prefix = `${year}-`
  const dates = new Set<string>()
  for (const adjacentYear of [year - 1, year, year + 1]) {
    for (const holiday of getUsFederalHolidaysForYear(adjacentYear)) {
      const date = kind === 'observed' ? holiday.observedDate : holiday.date
      if (date.startsWith(prefix)) dates.add(date)
    }
  }
  return [...dates].sort()
}
